import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {DELETE_MEDICATION_REQUEST, GET_DEFAULT_MEDICATIONS_REQUEST, GET_PATIENT_MEDICATIONS_REQUEST} from '../../../../../redux/actions';
import classes from './Medications.module.scss';
import {AddEditMedicationModal} from './components';
import {Modal} from '../../../../common/Modal/Modal';
import {IMedication} from '../../../../../utils/interfaces';
import {format} from 'date-fns';
import {PeriodToggle, PeriodToggleRef} from '../ClickingOverview/components/PeriodToggle/PeriodToggle';
import {Button} from '@mui/material';
import {Add} from '../../../../../assets/icons/Add';
import {MedicationsTable} from './components/MedicationsTable';
import {DatesPeriodType} from './components/MedicationsTable/types';

export type SelectedMedicationData = {
  type: 'edit' | 'add' | 'remove' | null
  medication: IMedication | null
}

export const Medications = ({
  patientId,
}: {
  patientId: string;
}) => {
  const dispatch = useDispatch();

  const [selectedMedicationData, setSelectedMedicationData] = useState<SelectedMedicationData>({
    type: null,
    medication: null,
  })

  const closeModal = () => setSelectedMedicationData({type: null, medication: null});
  const [dates, setDates] = useState<DatesPeriodType>(null);
  const periodToggleRef = useRef<PeriodToggleRef | null>(null);

  const deleteMedication = () => {
    if (selectedMedicationData.medication?.id) {
      dispatch({
        type: DELETE_MEDICATION_REQUEST,
        payload: {
          medicationId: selectedMedicationData.medication.id,
          patientId,
        }
      })
      closeModal()
      setTimeout(getPatientMedications, 300)
    }
  }

  const getPatientMedications = useCallback(() => {
    dispatch({
      type: GET_PATIENT_MEDICATIONS_REQUEST,
      payload: {
        patientId,
        startDate: format(dates?.start ?? new Date(), 'yyyy-MM-dd'),
        endDate: format(dates?.end ?? new Date(), 'yyyy-MM-dd'),
      }
    })
  }, [dispatch, patientId, dates]);

  useEffect(() => {
    getPatientMedications();
  }, [getPatientMedications])

  useEffect(() => {
    dispatch({
      type: GET_DEFAULT_MEDICATIONS_REQUEST,
    });
  }, [dispatch]);

  const onEditMedication = (medication: IMedication) => {
    setSelectedMedicationData({
      type: 'edit',
      medication,
    })
  }

  const onRemoveMedication = (medication: IMedication) => {
    setSelectedMedicationData({
      type: 'remove',
      medication,
    })
  }

  const onAddMedication = () => {
    setSelectedMedicationData({
      type: 'add',
      medication: null,
    })
  }

  const onAddMedicationForSpecificTimeslot = () => {
    setSelectedMedicationData({
      type: 'add',
      medication: null,
    })
  }

  return (
    <div className={classes.clickingOverviewContainer}>
      <div style={{display: 'flex', gap: 16}}>
        <PeriodToggle ref={periodToggleRef} setDates={setDates} />
        <div style={{flex: 1, display: 'flex', justifyContent: 'flex-end'}}>
          <Button
            onClick={onAddMedication}
            startIcon={<Add />}
            variant="contained"
            sx={{alignSelf: 'center'}}
          >
            Add medication
          </Button>
        </div>
      </div>
      <MedicationsTable
        onAddMedicationForSpecificTimeslot={onAddMedicationForSpecificTimeslot}
        onEditMedication={onEditMedication}
        onRemoveMedication={onRemoveMedication}
        period={dates} />
      <AddEditMedicationModal
        getPatientMedications={getPatientMedications}
        medication={selectedMedicationData.medication}
        onClose={closeModal}
        type={selectedMedicationData.type} />
      <Modal
        open={selectedMedicationData.type === 'remove'}
        title={"Delete medication"}
        message={
          "Are you sure that you want to delete this medication?"
        }
        handleCancel={closeModal}
        handleConfirm={deleteMedication}
      />
    </div>
  );
};
